import { getSelectedAgent } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { TextLog } from '@/types/TInbox';
import { capitalizeFirstLetter, renderPSTDate, safeJSONParse } from '@/utils/helper';
import { Popover, Steps } from 'antd';
import { AlertTriangle, Info } from 'lucide-react';
import React from 'react';

interface TextMessageDisplayProps {
	from: string;
	textLog: TextLog;
}

const TextMessageDisplay: React.FC<TextMessageDisplayProps> = ({ from, textLog }) => {
	const selectedAgent = useAppSelector(getSelectedAgent);

	const gerDeliveryStatus = (deliveryStatus?: string) => {
		const parsed = deliveryStatus ? safeJSONParse(deliveryStatus) : [];

		// Handle both formats: convert object to array if stored like that
		const st = Array.isArray(parsed) ? parsed : [parsed];

		if (st.length > 0) {
			const lastObj = st[st.length - 1];
			return {
				status: lastObj?.status || 'queued',
				timeUtc: lastObj?.timeUtc ?? null,
			};
		}
		return {
			status: 'queued',
			timeUtc: null,
		};
	};
	const deliveryInfo = gerDeliveryStatus(textLog?.deliveryStatus);

	const getStatusStyles = (status: string) => {
		const statusMap = {
			// Success - Green
			delivered: 'bg-green-50 text-green-700 border-green-200',
			sent: 'bg-green-50 text-green-700 border-green-200',
			received: 'bg-green-50 text-green-700 border-green-200',

			// In Progress - Blue
			queued: 'bg-blue-50 text-blue-700 border-blue-200',
			sending: 'bg-blue-50 text-blue-700 border-blue-200',
			receiving: 'bg-blue-50 text-blue-700 border-blue-200',

			// Error - Red
			failed: 'bg-red-50 text-red-700 border-red-200',
			undelivered: 'bg-red-50 text-red-700 border-red-200',
		};

		return statusMap[status] || 'bg-gray-50 text-gray-700 border-gray-200';
	};

	const getDotColor = (status: string) => {
		const dotMap = {
			delivered: 'bg-green-500',
			sent: 'bg-green-500',
			received: 'bg-green-500',
			queued: 'bg-blue-500',
			sending: 'bg-blue-500',
			receiving: 'bg-blue-500',
			failed: 'bg-red-500',
			undelivered: 'bg-red-500',
		};

		return dotMap[status] || 'bg-gray-500';
	};

	const renderDeliveryStatusDetails = () => {
		const parsed = textLog?.deliveryStatus ? safeJSONParse(textLog.deliveryStatus) : [];
		const statusArray = Array.isArray(parsed) ? parsed : [parsed];

		return (
			<Steps direction="vertical" size="small" current={statusArray.length - 1}>
				{statusArray.map((statusObj: any, index: number) => (
					<Steps.Step
						key={index}
						title={capitalizeFirstLetter(statusObj?.status)}
						description={renderPSTDate(statusObj?.timeUtc, selectedAgent?.time_zone, true)}
					/>
				))}
			</Steps>
		);
	};

	const ErrorContainer = ({ textLog }) => {
		if (!textLog?.errorText) {
			return null;
		}

		return (
			<div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg p-1">
				<div className="bg-white rounded-lg p-5 shadow-sm border border-red-100">
					<div className="flex items-start space-x-3">
						<div className="w-1 h-full bg-gradient-to-b from-red-400 to-orange-500 rounded-full min-h-[80px]" />
						<div className="flex-1 space-y-4">
							{/* Error Section */}
							<div>
								<div className="flex items-center space-x-2 mb-2">
									<AlertTriangle className="w-4 h-4 text-red-500" />
									<h4 className="text-sm font-semibold text-red-700 uppercase tracking-wide">
										Error
									</h4>
								</div>
								<div className="bg-red-50 border border-red-200 rounded-md p-3">
									<p className="text-red-800 text-sm leading-relaxed">{textLog.errorText}</p>
								</div>
							</div>

							{/* Resolution Section */}
							{textLog.errorResolve && (
								<div>
									<div className="flex items-center space-x-2 mb-2">
										<Info className="w-4 h-4 text-blue-500" />
										<h4 className="text-sm font-semibold text-blue-700 uppercase tracking-wide">
											How to Resolve
										</h4>
									</div>
									<div className="bg-blue-50 border border-blue-200 rounded-md p-3">
										<p className="text-blue-800 text-sm leading-relaxed whitespace-pre-wrap">
											{textLog.errorResolve}
										</p>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="space-y-6">
			<div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
				<div className="bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-4 border-b border-gray-100">
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-4">
							<div className="flex items-center space-x-3">
								<div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
									<span className="text-white font-semibold text-sm">{from.slice(-4)}</span>
								</div>
								<div>
									<p className="text-xs font-medium text-gray-500 uppercase tracking-wide">From</p>
									<p className="text-sm font-semibold text-gray-900">{from}</p>
								</div>
							</div>

							<div className="flex items-center px-4">
								<div className="flex items-center space-x-1">
									<div className="w-8 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400" />
									<div className="w-0 h-0 border-l-[6px] border-l-purple-500 border-y-[3px] border-y-transparent" />
									<div className="w-8 h-0.5 bg-gradient-to-r from-purple-400 to-purple-500" />
								</div>
							</div>

							<div className="flex items-center space-x-3">
								<div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center shadow-sm">
									<span className="text-white font-semibold text-sm">{textLog?.to?.slice(-4)}</span>
								</div>
								<div>
									<p className="text-xs font-medium text-gray-500 uppercase tracking-wide">To</p>
									<p className="text-sm font-semibold text-gray-900">{textLog?.to}</p>
								</div>
							</div>
						</div>

						<Popover
							content={renderDeliveryStatusDetails()}
							title="Delivery Status Timeline"
							trigger="hover"
							placement="bottom"
							overlayStyle={{ minWidth: '300px', maxWidth: '500px' }}
						>
							<div className="flex items-center space-x-4 cursor-pointer hover:bg-gray-100 p-2 rounded-md">
								<div className="flex items-center space-x-2">
									<div className={`w-2 h-2 rounded-full ${getDotColor(deliveryInfo.status)}`} />
									<span
										className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusStyles(deliveryInfo.status)}`}
									>
										{capitalizeFirstLetter(deliveryInfo.status)}
									</span>
								</div>
								<span className="text-xs text-gray-500 font-medium bg-gray-100 px-3 py-1 rounded-full">
									{renderPSTDate(deliveryInfo.timeUtc, selectedAgent?.time_zone)}
								</span>
							</div>
						</Popover>
					</div>
				</div>

				<div className="p-6">
					<div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-1">
						<div className="bg-white rounded-lg p-5 shadow-sm border border-gray-100">
							<div className="flex items-start space-x-3">
								<div className="w-1 h-full bg-gradient-to-b from-blue-400 to-purple-500 rounded-full min-h-[60px]" />
								<div className="flex-1">
									<div className="flex items-center justify-between mb-3">
										<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
											Message Content
										</h4>
										<span className="text-xs text-gray-400 font-mono">
											{textLog?.text?.length} characters
										</span>
									</div>
									<div className="max-h-[300px] overflow-y-auto">
										<p className="text-gray-800 leading-relaxed whitespace-pre-wrap text-sm">
											{textLog?.text}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
					<ErrorContainer textLog={textLog} />
				</div>
			</div>
		</div>
	);
};

export default TextMessageDisplay;
